import type { FunctionComponent, MouseEvent } from 'react';
import { useCallback } from 'react';
import cx from 'classnames';

import { CardFrontTestIds } from '@trello/test-ids';

import { isModifierKeyPressed } from 'app/src/isModifierKeyPressed';

import styles from './CardFrontName.less';

// Minimal cards with undefined names should still render a non-breaking
// space character, for a baseline estimate of height.
const NON_BREAKING_SPACE_CHARACTER = '\u00A0';

interface Props {
  name?: string;
  url?: string;
  className?: string;
}

export const CardFrontName: FunctionComponent<Props> = ({
  name = NON_BREAKING_SPACE_CHARACTER,
  url = '',
  className,
}) => {
  /**
   * The card name is rendered as an anchor tag for accessibility, but we don't
   * actually want to rely on it. When it's clicked, just prevent default, and
   * let the click callback for the card container component handle navigation.
   */
  const onClickName = useCallback((e: MouseEvent) => {
    if (!isModifierKeyPressed(e)) {
      e.preventDefault();
    }
  }, []);

  return (
    <a
      draggable="false"
      className={cx(styles.name, className)}
      dir="auto"
      href={url}
      onClick={onClickName}
      data-testid={CardFrontTestIds.CardName}
    >
      {name}
    </a>
  );
};
